import React, { useEffect, useState } from 'react';

import { AccountCircle } from '@mui/icons-material';
import CalculateIcon from '@mui/icons-material/Calculate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import {
  InputAdornment,
  MenuItem,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import { Province } from '../../@types/KingdomResponse';
import { ProvinceLite, SimState } from '../../@types/State';
import { useAppState } from '../../context/AppState';
import { useIntelState } from '../../context/IntelState';
import {
  fixBuildPercentage,
  getSimStateFromProvince,
} from '../../helpers/SimMapper';
import { getGameState } from '../../helpers/SimStateCalculator';
import { useOwnData } from '../../hooks/useOwnData';
import HomesCalculations from '../Simulation/HomesCalculations';
import SimulationForm from '../Simulation/SimulationForm';
import StateCalculations from '../Simulation/StateCalculations';
import TimeLapseCalculations from '../Simulation/TimeLapseCalculations';
import TrainingTick from '../Simulation/TrainingTick';

const Simulation = () => {
  const { appState, appDispatch } = useAppState();
  const { intelState, intelDispatch } = useIntelState();
  const [simState, setSimState] = useState<SimState>(
    fixBuildPercentage(appState.simStates[0])
  );
  const [expanded, setExpanded] = useState<string>('');
  const [buildIndex, setBuildIndex] = useState<number>(0);
  const [province, setProvince] = useState<ProvinceLite | undefined>(undefined);
  const [provinces, setProvinces] = useState<ProvinceLite[] | undefined>(
    undefined
  );
  const showCalcs = true;
  const gameState = getGameState(simState);
  useOwnData();
  useEffect(() => {
    const provinces = intelState.ownData?.provinces.map((x: Province) => {
      return {
        id: x.id,
        name: x.name,
        raceCombo: `${x.race} ${x.sot?.personality}`,
        race: x.race,
        slot: `[${x.id}]`,
      } as ProvinceLite;
    });
    setProvinces(provinces);
    setProvince(undefined);
  }, [intelState.ownData, intelState.server]);

  const handleAccordionChange =
    (panel: string) => (event: any, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : '');
    };

  const handleBuildChange = (event: any) => {
    setBuildIndex(event.target.value);
    const newBuild = appState.simStates[event.target.value];
    if (newBuild.server && intelState.server !== newBuild.server) {
      intelDispatch({
        type: 'SET_SERVER',
        payload: newBuild.server,
      });
    }
    setSimState(
      fixBuildPercentage({ ...appState.simStates[event.target.value] })
    );
  };

  const updateSimState = (field: string, value: any) => {
    const newState = {
      ...simState,
      [field]: value,
    };
    setSimState(newState);
  };

  const handleSaveBuid = () => {
    const newStates = appState.simStates;
    newStates[buildIndex] = { ...simState, server: intelState.server };
    appDispatch({ type: 'SET_SIM_STATE', payload: newStates });
  };

  const handleImport = () => {
    const selectedProvince = intelState.ownData?.provinces.find(
      (x: Province) => x.id === province?.id
    );
    if (!selectedProvince) return;
    const mappedState = getSimStateFromProvince(selectedProvince);
    const newState = {
      ...simState,
      ...mappedState,
      raceCombo: province?.raceCombo,
    };
    setSimState(newState);
  };

  const handleProvinceChange = (event: any) => {
    const prov: ProvinceLite | undefined = provinces?.find(
      (x: ProvinceLite) => x.id === event.target.value
    );
    if (prov) {
      setProvince(prov);
    }
  };

  return (
    <div className='Simulation'>
      <Box sx={{ m: 2 }}>
        <Typography variant='h5' gutterBottom component='div'>
          Build Tester - Work in progress - FORMULAS BEING UPDATED
        </Typography>
        <Typography variant='body1'>
          You can save 5 builds to the browser cache. The Timelapse feature is
          to calcuate economy over i.e. GCs and Soldier draft.
        </Typography>
      </Box>
      <Box sx={{ m: 2 }}>
        <FormControl variant="standard">
          <RadioGroup
            aria-labelledby='demo-controlled-radio-buttons-group'
            name='controlled-radio-buttons-group'
            value={buildIndex}
            onChange={handleBuildChange}
            row
          >
            {appState.simStates.map((x: SimState) => (
              <FormControlLabel
                key={x.buildIndex}
                value={x.buildIndex}
                control={<Radio />}
                label={x.buildName}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
      <Box sx={{ m: { xs: 0, sm: 1 } }}>
        <Accordion
          expanded={expanded === 'build'}
          onChange={handleAccordionChange('build')}
          TransitionProps={{ unmountOnExit: true }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              <Button variant='outlined' size='small'>
                {expanded === 'build' ? 'Hide' : 'Show'}
              </Button>{' '}
              Set Build
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid
              container
              spacing={1}
              alignItems='center'
              columns={{ xs: 4, sm: 8, md: 8 }}
              rowSpacing={2}
            >
              <Grid size={2}>
                <TextField
                  variant="standard"
                  size='small'
                  type='text'
                  label={'Build Name'}
                  value={simState['buildName']}
                  onChange={(e) => updateSimState('buildName', e.target.value)}
                  fullWidth />
              </Grid>
              <Grid size={2}>
                <Button
                  variant='outlined'
                  size='small'
                  onClick={handleSaveBuid}
                  startIcon={<SaveIcon />}
                >
                  Save Build
                </Button>
              </Grid>
              <Grid size={2}>
                <TextField
                  variant="standard"
                  name='province-select'
                  select
                  label='Select Province'
                  aria-label='Select Province'
                  value={province ? province.id : ''}
                  size='small'
                  sx={{ marginRight: '10px', minWidth: '150px' }}
                  onChange={handleProvinceChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <AccountCircle />
                      </InputAdornment>
                    ),
                  }}>
                  {provinces?.map((p, i) => (
                    <MenuItem key={i} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={2}>
                <Button
                  variant='outlined'
                  disabled={!province}
                  size='small'
                  onClick={handleImport}
                  startIcon={<CalculateIcon />}
                >
                  Import Province
                </Button>
              </Grid>
              {/* <Grid item>
                <Button
                  variant='outlined'
                  size='small'
                  onClick={() => setShowCalcs(!showCalcs)}
                  startIcon={<CalculateIcon />}
                >
                  {showCalcs ? 'Hide' : 'Show'} Calcs
                </Button>
              </Grid> */}
            </Grid>
            <Grid container spacing={2} mt={2}>
              <Grid
                size={{
                  sm: 12,
                  md: 6,
                  lg: 8
                }}>
                <SimulationForm
                  simState={simState}
                  updateSimState={updateSimState}
                />
              </Grid>
              <Grid
                size={{
                  sm: 12,
                  md: 6,
                  lg: 4
                }}>
                <StateCalculations state={gameState} showCalcs={showCalcs} />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'homes'}
          onChange={handleAccordionChange('homes')}
          TransitionProps={{ unmountOnExit: true }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              <Button variant='outlined' size='small'>
                {expanded === 'homes' ? 'Hide' : 'Show'}
              </Button>{' '}
              Calculate Homes
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <HomesCalculations simState={simState} />
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded === 'simulate'}
          onChange={handleAccordionChange('simulate')}
          TransitionProps={{ unmountOnExit: true }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              <Button variant='outlined' size='small'>
                {expanded === 'simulate' ? 'Hide' : 'Show'}
              </Button>{' '}
              EOWCF TimeLapse
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TimeLapseCalculations
              simState={simState}
              gameState={gameState}
              updateSimState={updateSimState}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded === 'training'}
          onChange={handleAccordionChange('training')}
          TransitionProps={{ unmountOnExit: true }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              <Button variant='outlined' size='small'>
                {expanded === 'training' ? 'Hide' : 'Show'}
              </Button>{' '}
              Training Tick
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TrainingTick
              simState={simState}
              gameState={gameState}
              updateSimState={updateSimState}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
    </div>
  );
};

export default Simulation;
